import React, { useState, useRef, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { motion } from "framer-motion";
import swal from "sweetalert";
import { useHistory } from "react-router-dom";

import makeAnimated from "react-select/animated";
import SidePanel from "./SidePanel.jsx";
import DeletePopup from "../../elements/DeletePopup";
import SuccessPopup from "../../elements/SuccessPopup";
import { ReactComponent as LinkIcon } from "../../assets/icons/Link.svg";
import { Button } from "../../elements/Button";
import commoncss from "../../css/common.module.css";
import buttoncss from "../../css/button.module.css";
import { ReactComponent as FilterIcon } from "../../assets/icons/filter.svg";
import { ReactComponent as PlusIcon } from "../../assets/icons/AddPlus.svg";
import { ReactComponent as ExportIcon } from "../../assets/icons/download.svg";
import { ReactComponent as CirclePlus } from "../../assets/icons/circlePlus.svg";
import { ReactComponent as AddEditor } from "../../assets/icons/addEditor.svg";
import { ReactComponent as AddTable } from "../../assets/icons/addTable.svg";
import libcss from "../../css/library.module.css";
import MenuIcon from "../../assets/icons/Menu.png";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  createDisclosureApiHandler,
  createSubDisclosureApiHandler,
  updateDisclosureApiHandler,
  getTemplateDetailsApiHandler,
  updateSubDisclosureApiHandler,
  deleteDisclosureApiHandler,
  deleteSubDisclosureApiHandler,
} from "../../../WorkSpace/Components/ApiServices/apiHandler";
import { ReactComponent as Plus } from "../../../myfin_v2/assets/icons/plusprimary.svg";
import { ReactComponent as EditIcon } from "../../../myfin_v2/assets/icons/editPri.svg";
import { ReactComponent as CloseIcon } from "../../../myfin_v2/assets/icons/close.svg";
import { ReactComponent as DelIcon } from "../../../myfin_v2/assets/icons/delete_red.svg";
import TemplateTableGenerator from "./TemplateTableGenerator";
import QuillReactEditor from "../../components/QuillReactEditor";


import CustomTable from "../../../Console/Component/CustomTable/CustomTable";

const genId = () => Math.random().toString(36).slice(2);

const normalizeFromInitial = (initialDoc) => {
  const items = [];
  (initialDoc?.data || []).forEach((block) => {
    if (block.dataType === "text") {
      items.push({
        id: block._id || genId(),
        _id: block._id,
        type: "text",
        dataType: "text",
        textFormat: "html",
        textRaw: block?.textRaw ?? " ",
        textPlain: block?.textPlain ?? " ",
        text: Array.isArray(block.text) && block.text.length > 0
          ? block.text.map((t) => ({ ...t, id: t.id || genId() }))
          : [
              {
                id: genId(),
                content: block?.textRaw ?? " ",
              },
            ],
      });
    } else if (block.dataType === "table") {
      const tableRow = Array.isArray(block.tableRow)
        ? block.tableRow.map((r) => ({ ...r }))
        : [];
      const tableColumn = Array.isArray(block.tableColumn)
        ? block.tableColumn.map((c) => ({ ...c }))
        : [];
      const rawTable = Array.isArray(block?.table)
        ? block.table.map((r) => ({ ...r }))
        : Array.isArray(block?.tableData)
        ? block.tableData.map((r) => ({ ...r }))
        : null;

      items.push({
        id: block._id || genId(),
        _id: block._id,
        type: "table",
        dataType: "table",
        tableTitle: block.tableTitle || "Untitled Table",
        tableRow,
        tableColumn,
        tableType: block?.tableType ?? "content",
        table: rawTable,
        tableData: rawTable,
        hasCellData:
          block?.tableType === "content" ||
          (Array.isArray(rawTable) && rawTable.length > 0),
        cellFormulas: Array.isArray(block?.cellFormulas)
          ? block.cellFormulas.map((f) => ({ ...f }))
          : [],
      });
    }
  });
  return items;
};

// Serialize the flat list back to your exact schema
const serialize = (name, items) => {
  const data = (items || []).map((it) => {
    if (it.type === "text" || it.dataType === "text") {
      return {
        ...(it._id && { _id: it._id }),
        dataType: "text",
        textFormat: it.textFormat || "html",
        textRaw: it?.textRaw ?? " ",
        textPlain: it?.textPlain ?? " ",
        text: Array.isArray(it.text) && it.text.length > 0
          ? it.text.map((t) => ({
              ...(t._id && { _id: t._id }),
              content: typeof t === "string" ? t : (t?.content ?? " "),
            }))
          : [
              {
                content: it?.textRaw ?? " ",
              },
            ],
      };
    }
    if (it.type === "table" || it.dataType === "table") {
      const hasCellData =
        it.hasCellData !== undefined
          ? it.hasCellData
          : (it.tableType === "content" ||
              (Array.isArray(it.table) && it.table.length > 0) ||
              (Array.isArray(it.tableData) && it.tableData.length > 0));

      const tableRow = (it.tableRow || []).map((row) => {
        const rowObj = {
          name: row.name ?? "",
          lineType: row.lineType || "line item",
        };

        if (row._id && !row.isNewRow) {
          rowObj._id = row._id;
        }

        if (row.formulaType) {
          rowObj.formulaType = row.formulaType;
        }

        if (row.isBold !== undefined) {
          rowObj.isBold = row.isBold;
        }

        if (
          row.lineType === "formula" ||
          (Array.isArray(row.formula) && row.formula.length > 0)
        ) {
          rowObj.lineType = "formula";
          rowObj.formula = Array.isArray(row.formula)
            ? row.formula.map((f) => ({
                name: f.name || "",
                sign: f.sign === "negative" ? "negative" : "positive",
              }))
            : [];
        }

        return rowObj;
      });

      const tableColumn = (it.tableColumn || []).map((col) => {
        const colObj = {
          name: col.name ?? "",
          lineType: col.lineType || "line item",
        };

        if (col._id) {
          colObj._id = col._id;
        }

        if (
          col.lineType === "formula" ||
          (Array.isArray(col.formula) && col.formula.length > 0)
        ) {
          colObj.lineType = "formula";
          colObj.formula = Array.isArray(col.formula)
            ? col.formula.map((f) => ({
                name: f.name || "",
                sign: f.sign === "negative" ? "negative" : "positive",
              }))
            : [];
        }

        return colObj;
      });

      const tableObj = {
        dataType: "table",
        tableTitle: it.tableTitle || "Untitled Table",
        ...(it._id && { _id: it._id }),
        tableRow,
        tableColumn,
        cellFormulas: Array.isArray(it.cellFormulas)
          ? it.cellFormulas.map((f) => ({ ...f }))
          : [],
      };

      if (hasCellData) {
        tableObj.tableType = "content";
        const rawTableData = it.tableData || it.table || [];
        tableObj.table = Array.isArray(rawTableData)
          ? rawTableData.map((row) => {
              if (typeof row !== "object" || row === null) return {};
              const cleanRow = {};
              Object.keys(row).forEach((k) => {
                const lower = k.toLowerCase();
                if (
                  lower === "isnewrow" ||
                  lower === "isnew" ||
                  lower === "isedited"
                ) {
                  return;
                }
                cleanRow[k] = row[k];
              });
              return cleanRow;
            })
          : [];
      }

      return tableObj;
    }
    return it;
  });

  return { name: name?.trim() || "Untitled", data };
};

export default function Disclosures({
  tempId,
  setHandleSave,
  handleSave,
  isReadOnly = false,
}) {
  // ── Note inline-edit state ─────────────────────────────────────────────────
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // ── Sub-note inline-edit state (single declaration) ───────────────────────
  const [isSubEditing, setIsSubEditing] = useState(false);
  const [editingSubId, setEditingSubId] = useState(null);
  const [editingSubValue, setEditingSubValue] = useState("");
  const [editingParentId, setEditingParentId] = useState(null);

  // ── Side panel state ───────────────────────────────────────────────────────
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [linkedCells, setLinkedCells] = useState({});
  const [activeCellKey, setActiveCellKey] = useState(null);
  const [selectedDataID, setSelectedDataID] = useState(null);
  const [isAddingTable, setIsAddingTable] = useState(false);

  const DISCLOSURE_LINK_COL_NAMES = ["cy", "py", "current year", "previous year", "currentyear", "previousyear"];
  const DISCLOSURE_COL_API_MAP = {
    "cy": "currentYear",
    "py": "previousYear",
    "current year": "currentYear",
    "previous year": "previousYear",
    "currentyear": "currentYear",
    "previousyear": "previousYear",
  };

  const handleCellLinkClick = (
    row,
    col,
    dataID,
    tableItem,
    currentRowLabel,
    tableRows
  ) => {
    if (row === 0) return;
    if (col === 0) return; // ← first column is always row-label, never linkable

    // try to get column name from tableColumn
    const colName = String(
      tableItem?.tableColumn?.[col]?.name ?? ""
    ).toLowerCase().trim();

    let resolvedCol = null;

    if (colName) {
      const matchedKey = DISCLOSURE_LINK_COL_NAMES.find((n) => colName.includes(n));
      if (!matchedKey) return;
      resolvedCol = DISCLOSURE_COL_API_MAP[matchedKey];
    } else {
      // tableColumn unavailable (cell-data mode table) — use col index
      // col 1 = currentYear, col 2 = previousYear by convention
      if (col === 1) resolvedCol = "currentYear";
      else if (col === 2) resolvedCol = "previousYear";
      else return;
    }

    const labelFromProp = currentRowLabel && String(currentRowLabel).trim();
    const metaRowName =
      tableRows?.[row - 1]?.name && String(tableRows[row - 1].name).trim();
    const itemRowName =
      tableItem?.tableRow?.[row - 1]?.name &&
      String(tableItem.tableRow[row - 1].name).trim();
    const itemDataName = String(
      tableItem?.tableData?.[row - 1]?.particular ||
      tableItem?.tableData?.[row - 1]?.Particulars ||
      tableItem?.tableData?.[row - 1]?.name ||
      tableItem?.tableData?.[row - 1]?.column_0 ||
      tableItem?.table?.[row - 1]?.particular ||
      tableItem?.table?.[row - 1]?.Particulars ||
      tableItem?.table?.[row - 1]?.name ||
      tableItem?.table?.[row - 1]?.column_0 ||
      ""
    ).trim();

    const rowName =
      labelFromProp ||
      metaRowName ||
      itemRowName ||
      itemDataName ||
      String(row);

    const cellKey = `${dataID}-${row}-${col}`;
    setActiveCellKey(cellKey);
    setSelectedCell({ row: rowName, col: resolvedCol });
    setSelectedDataID(dataID);
    setIsSidePanelOpen(true);
  };

  // ── Editor / items state ───────────────────────────────────────────────────
  const [name, setName] = useState("");
  const [items, setItems] = useState(() => normalizeFromInitial(null));
  const [output, setOutput] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [forceOpenTableId, setForceOpenTableId] = useState(null);

  // ── Note list / composer state ─────────────────────────────────────────────
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const animatedComponents = makeAnimated();
  let history = useHistory();
  const dispatch = useDispatch();
  const [libraryName, setLibraryName] = useState("ST Library");
  const [activeItem, setActiveItem] = useState(null);
  const [selNote, setSelNote] = useState(null);
  const [selNoteId, setSelNoteId] = useState(null);
  const [selParentId, setSelParentId] = useState(null);
  const [notes, setNotes] = useState(null);
  const [note, setNote] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [showPanel, setShowPanel] = useState(false);
  const [activeCell, setActiveCell] = useState(null);

  const openPanel = (row, col) => {
    setActiveCell({ row, col });
    setShowPanel(true);
  };

  const [isComposing, setIsComposing] = useState(false);
  const [noteName, setNoteName] = useState("");
  const inputRef = useRef(null);

  const [templateData, setTemplateData] = useState(null);

  // ── API calls ──────────────────────────────────────────────────────────────
  const getTemplateDetails = (updatedTemplate) => {
    if (updatedTemplate && updatedTemplate.disclosure) {
      setTemplateData(updatedTemplate);
      return;
    }
    getTemplateDetailsApiHandler({ groupingTemplateID: tempId })
      .then((response) => {
        if (response?.data?.success === true) {
          setTemplateData(response?.data?.groupingTemplate);
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    getTemplateDetails();
  }, []);

  useEffect(() => {
    getTemplateDetails();
  }, [activeItem]);

  useEffect(() => {
    if (notes && notes.length > 0 && !selNote) {
      const firstNote = notes[0];
      setItems(normalizeFromInitial(firstNote));
      setActiveItem(`gs_0`);
      setSelNote("general");
      setSelNoteId(firstNote._id);
      setSelParentId(null);
      setOutput(firstNote);
      setName(firstNote.name);
    }
  }, [notes, selNote]);



  // ── Auto-save and sync refs ────────────────────────────────────────────────
  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const nameRef = useRef(name);
  useEffect(() => {
    nameRef.current = name;
  }, [name]);

  const debounceTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const saveDisclosureData = useCallback((itemsToSave, isAutoSave = false) => {
    if (!selNoteId) return Promise.resolve();
    const currentTemplateId = templateData?._id || tempId;
    if (!currentTemplateId) return Promise.resolve();

    const currentItems = itemsToSave || itemsRef.current;
    const currentName = nameRef.current || name;
    const payload = serialize(currentName, currentItems);

    const apiCall =
      selNote === "general_sn"
        ? updateSubDisclosureApiHandler(
            {
              groupingTemplateID: currentTemplateId,
              disclosureID: selParentId,
              subNoteID: selNoteId,
            },
            payload
          )
        : updateDisclosureApiHandler(
            {
              groupingTemplateID: currentTemplateId,
              disclosureID: selNoteId,
            },
            payload
          );

    return apiCall
      .then((response) => {
        if (response?.data?.success === true) {
          if (!isAutoSave) {
            setSuccessMessage("Saved successfully");
            setShowSuccessPopup(true);
          }
          const updatedTemplate =
            response?.data?.updatedGroupingTemplate ||
            response?.data?.groupingTemplate;
          if (updatedTemplate) {
            setTemplateData(updatedTemplate);
          } else {
            getTemplateDetails();
          }
        } else {
          if (!isAutoSave) {
            swal("Error", response?.data?.error || "Unable to save data", "error");
          }
        }
      })
      .catch((err) => {
        if (!isAutoSave) {
          swal(
            "Error",
            err?.response?.data?.error || err?.message || "Unable to save data",
            "error"
          );
        }
      });
  }, [selNoteId, templateData?._id, tempId, name, selNote, selParentId]);

  const debouncedSave = useCallback((itemsToSave) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      saveDisclosureData(itemsToSave, true);
    }, 1200);
  }, [saveDisclosureData]);

  // ── Editor helpers ─────────────────────────────────────────────────────────
  const handleAddParagraph = () => {
    const nextItems = [
      ...items,
      {
        id: genId(),
        type: "text",
        dataType: "text",
        text: [{ id: genId(), content: "" }],
        textFormat: "html",
        textRaw: " ",
        textPlain: " ",
      },
    ];
    setItems(nextItems);
    saveDisclosureData(nextItems, true);
  };

  const handleAddTable = () => {
    setIsAddingTable(true);
  };

  const handleAddSubRow = (tableId) => {
    if (!selNoteId) return;

    const tableItem = items.find((it) => it.id === tableId || it._id === tableId);
    if (!tableItem) return;

    const columns = tableItem.tableColumn || [];

    const newRowData = {
      name: "",
      isNewRow: true,
      isEdited: false,
    };

    if (columns.length > 0) {
      columns.forEach((col, idx) => {
        const key = col.name || `column_${idx}`;
        if (key) {
          newRowData[key] = "";
        }
      });
    }

    if (Array.isArray(tableItem.table) && tableItem.table.length > 0) {
      Object.keys(tableItem.table[0]).forEach((k) => {
        const lower = k.toLowerCase();
        if (
          lower !== "_id" &&
          lower !== "id" &&
          lower !== "isnewrow" &&
          lower !== "isnew" &&
          lower !== "isedited"
        ) {
          newRowData[k] = "";
        }
      });
    } else if (Array.isArray(tableItem.tableData) && tableItem.tableData.length > 0) {
      Object.keys(tableItem.tableData[0]).forEach((k) => {
        const lower = k.toLowerCase();
        if (
          lower !== "_id" &&
          lower !== "id" &&
          lower !== "isnewrow" &&
          lower !== "isnew" &&
          lower !== "isedited"
        ) {
          newRowData[k] = "";
        }
      });
    }

    const newTableRow = {
      name: "",
      lineType: "line item",
      isNewRow: true,
      isEdited: false,
    };

    const updatedItems = items.map((it) => {
      if (it.id === tableId || it._id === tableId) {
        const baseTableRow = it.tableRow || [];
        const baseTableData =
          it.tableData && it.tableData.length === baseTableRow.length
            ? it.tableData
            : it.table && it.table.length === baseTableRow.length
            ? it.table
            : baseTableRow.map((r) => ({ name: r.name || "" }));

        const nextTableRow = [...baseTableRow, newTableRow];
        const nextTableData = [...baseTableData, newRowData];

        return {
          ...it,
          tableRow: nextTableRow,
          tableData: nextTableData,
          table: nextTableData,
          hasCellData: true,
        };
      }
      return it;
    });

    setItems(updatedItems);
    saveDisclosureData(updatedItems, true);
  };

  const handleDeleteTableRow = (tableId, rowIndex, rowData) => {
    const updatedItems = items.map((it) => {
      if (it.id === tableId || it._id === tableId) {
        const nextTableRow = (it.tableRow || []).filter(
          (_, idx) => idx !== rowIndex - 1
        );
        const baseTableData = it.tableData || it.table || [];
        const nextTableData = baseTableData.filter(
          (_, idx) => idx !== rowIndex - 1
        );
        return {
          ...it,
          tableRow: nextTableRow,
          tableData: nextTableData,
          table: nextTableData,
        };
      }
      return it;
    });

    setItems(updatedItems);
    saveDisclosureData(updatedItems, true);
  };

  const getRowPermissions = useCallback(() => ({
    allowEdit: !isReadOnly,
    allowDelete: !isReadOnly,
    allowRearrange: !isReadOnly,
  }), [isReadOnly]);

  const handleTableChange = (tableId, next, lastChangedRow) => {
    setItems((prev) => {
      let isAnyRowReordered = false;
      const updated = prev.map((it) => {
        if (it.id === tableId || it._id === tableId) {
          const prevRows = it.tableRow || [];
          const nextRows = next.tableRow || [];
          const prevData = it.tableData || it.table || [];
          const nextData =
            next.tableData || next.table || it.tableData || it.table || [];

          // Detect row reorder
          const prevRowKeys = prevRows.map((r) =>
            String(r?._id || r?.id || r?.name || "")
          );
          const nextRowKeys = nextRows.map((r) =>
            String(r?._id || r?.id || r?.name || "")
          );
          const isSameRowCount =
            prevRowKeys.length === nextRowKeys.length && prevRowKeys.length > 0;
          const isRowReorder =
            isSameRowCount &&
            prevRowKeys.every((k) => nextRowKeys.includes(k)) &&
            prevRowKeys.some((k, i) => k !== nextRowKeys[i]);

          if (isRowReorder) {
            isAnyRowReordered = true;
            // Remap local coordinates in linkedCells when rearranged
            setLinkedCells((prevLinked) => {
              const nextLinked = { ...prevLinked };
              const tId = it._id || it.id;
              prevRows.forEach((prevR, oldIndex) => {
                const prevKey = String(
                  prevR?._id || prevR?.id || prevR?.name || ""
                );
                const newIndex = nextRows.findIndex(
                  (nextR) =>
                    String(nextR?._id || nextR?.id || nextR?.name || "") ===
                    prevKey
                );
                if (newIndex >= 0 && newIndex !== oldIndex) {
                  [1, 2, "currentYear", "previousYear"].forEach((col) => {
                    const oldCellKey = `${tId}-${oldIndex + 1}-${col}`;
                    const newCellKey = `${tId}-${newIndex + 1}-${col}`;
                    if (nextLinked[oldCellKey]) {
                      nextLinked[newCellKey] = nextLinked[oldCellKey];
                    }
                  });
                }
              });
              return nextLinked;
            });

            return {
              ...it,
              tableTitle:
                next.tableTitle !== undefined ? next.tableTitle : it.tableTitle,
              tableRow: nextRows,
              tableColumn: next.tableColumn || it.tableColumn,
              hasCellData:
                next.hasCellData !== undefined
                  ? next.hasCellData
                  : it.hasCellData,
              tableData: nextData,
              table: nextData,
              tableType: next.hasCellData
                ? "content"
                : it.tableType || "table",
            };
          }

          // Not a row reorder: detect row renames and update cellFormulas + linkedCells
          let updatedCellFormulas = Array.isArray(it.cellFormulas)
            ? it.cellFormulas.map((f) => ({ ...f }))
            : [];
          const renamedPairs = [];

          const maxRows = Math.max(
            prevRows.length,
            nextRows.length,
            prevData.length,
            nextData.length
          );

          for (let idx = 0; idx < maxRows; idx++) {
            const oldRow = prevRows[idx] || {};
            const newRow = nextRows[idx] || {};
            const oldDataRow = prevData[idx] || {};
            const newDataRow = nextData[idx] || {};

            const oldName = String(
              oldRow?.name ??
              oldDataRow?.particular ??
              oldDataRow?.Particulars ??
              oldDataRow?.name ??
              oldDataRow?.column_0 ??
              ""
            ).trim();

            const newName = String(
              newRow?.name ??
              newDataRow?.particular ??
              newDataRow?.Particulars ??
              newDataRow?.name ??
              newDataRow?.column_0 ??
              ""
            ).trim();

            if (newName && oldName !== newName) {
              renamedPairs.push({
                oldName,
                newName,
                rowIndex: idx + 1,
              });
            }
          }

          if (renamedPairs.length > 0) {
            renamedPairs.forEach(({ oldName, newName, rowIndex }) => {
              // 1. Update cellFormulas so formula.row matches the renamed row
              updatedCellFormulas = updatedCellFormulas.map((f) => {
                const fRow = String(f?.row ?? "").trim();
                const matchesOldName =
                  oldName && fRow.toLowerCase() === oldName.toLowerCase();
                const matchesRowIndex = fRow === String(rowIndex);
                const matchesEmpty = !fRow && !oldName;

                if (matchesOldName || matchesRowIndex || matchesEmpty) {
                  return { ...f, row: newName };
                }
                return f;
              });

              // 2. Update linkedCells immediately so link / formula icon stays visible
              setLinkedCells((prevLinked) => {
                const nextLinked = { ...prevLinked };
                const tId = it._id || it.id;
                Object.keys(nextLinked).forEach((key) => {
                  if (oldName) {
                    const prefixOld = `${tId}-${oldName}-`;
                    if (key.startsWith(prefixOld)) {
                      const colPart = key.slice(prefixOld.length);
                      const newKey = `${tId}-${newName}-${colPart}`;
                      nextLinked[newKey] = {
                        ...nextLinked[key],
                        row: newName,
                      };
                    }
                  }
                  const prefixIdx = `${tId}-${rowIndex}-`;
                  if (key.startsWith(prefixIdx)) {
                    const colPart = key.slice(prefixIdx.length);
                    const resolvedCol =
                      colPart === "1"
                        ? "currentYear"
                        : colPart === "2"
                        ? "previousYear"
                        : colPart;
                    const newKey = `${tId}-${newName}-${resolvedCol}`;
                    nextLinked[newKey] = {
                      ...nextLinked[key],
                      row: newName,
                    };
                  }
                });
                return nextLinked;
              });

              // 3. Add oldName to linkedNameAliases on nextRows so TemplateTableGenerator fallback succeeds
              if (oldName && nextRows[rowIndex - 1]) {
                nextRows[rowIndex - 1].linkedNameAliases = Array.from(
                  new Set([
                    oldName,
                    ...(nextRows[rowIndex - 1].linkedNameAliases || []),
                    ...(prevRows[rowIndex - 1]?.linkedNameAliases || []),
                  ])
                );
              }

              // 4. Update row formula operands referencing oldName
              nextRows.forEach((r) => {
                if (Array.isArray(r.formula)) {
                  r.formula = r.formula.map((term) => {
                    if (
                      term.name &&
                      oldName &&
                      term.name.trim().toLowerCase() === oldName.toLowerCase()
                    ) {
                      return { ...term, name: newName };
                    }
                    return term;
                  });
                }
              });

              // 5. Update templateData state so background effects don't revert to old names
              setTemplateData((prevTemp) => {
                if (!prevTemp?.disclosure) return prevTemp;
                const nextTemp = { ...prevTemp };
                nextTemp.disclosure = nextTemp.disclosure.map((n) => {
                  const updateBlock = (b) => {
                    if ((b._id || b.id) !== (it._id || it.id)) return b;
                    const nextFormulas = (b.cellFormulas || []).map((f) => {
                      const fRow = String(f?.row ?? "").trim();
                      if (
                        (oldName &&
                          fRow.toLowerCase() === oldName.toLowerCase()) ||
                        fRow === String(rowIndex) ||
                        (!fRow && !oldName)
                      ) {
                        return { ...f, row: newName };
                      }
                      return f;
                    });
                    return { ...b, cellFormulas: nextFormulas };
                  };
                  const nData = (n.data || []).map(updateBlock);
                  const subNotes = (n.subNotes || []).map((sn) => ({
                    ...sn,
                    data: (sn.data || []).map(updateBlock),
                  }));
                  return { ...n, data: nData, subNotes };
                });
                return nextTemp;
              });
            });
          }

          return {
            ...it,
            tableTitle:
              next.tableTitle !== undefined ? next.tableTitle : it.tableTitle,
            tableRow: nextRows || it.tableRow,
            tableColumn: next.tableColumn || it.tableColumn,
            hasCellData:
              next.hasCellData !== undefined
                ? next.hasCellData
                : it.hasCellData,
            tableData: nextData,
            table: nextData,
            tableType: next.hasCellData
              ? "content"
              : it.tableType || "table",
            cellFormulas: updatedCellFormulas,
          };
        }
        return it;
      });

      if (isAnyRowReordered) {
        saveDisclosureData(updated, true);
      } else {
        debouncedSave(updated);
      }
      return updated;
    });
  };

  const handleCellBlurAutoSave = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setItems((currentItems) => {
      saveDisclosureData(currentItems, true);
      return currentItems;
    });
  };

  const removeItem = (id) => {
    const target = items.find((it) => it.id === id || it._id === id);
    if (!target) return;

    if (!templateData?._id || !selNoteId) {
      swal("Error", "Template data not loaded yet", "error");
      return;
    }

    setDeleteConfirm({
      title: "Are you sure?",
      message: "This item will be deleted permanently",
      onConfirm: () => {
        setDeleteConfirm(null);
        const filteredItems = items.filter(
          (it) => it.id !== id && it._id !== id
        );
        const payload = serialize(name, filteredItems);

        const apiCall =
          selNote === "general_sn"
            ? updateSubDisclosureApiHandler(
                {
                  groupingTemplateID: templateData._id,
                  disclosureID: selParentId,
                  subNoteID: selNoteId,
                },
                payload
              )
            : updateDisclosureApiHandler(
                {
                  groupingTemplateID: templateData._id,
                  disclosureID: selNoteId,
                },
                payload
              );

        apiCall
          .then((response) => {
            if (response?.data?.success === true) {
              setItems(filteredItems);
              setSuccessMessage("Successfully deleted");
              setShowSuccessPopup(true);
              const updatedTemplate =
                response?.data?.updatedGroupingTemplate ||
                response?.data?.groupingTemplate;
              if (updatedTemplate) {
                setTemplateData(updatedTemplate);
              } else {
                getTemplateDetails();
              }
            } else {
              swal(
                "Error",
                response?.data?.error || "Unable to delete the item",
                "error"
              );
            }
          })
          .catch((error) => {
            swal(
              "Error",
              error?.response?.data?.error ||
                error?.message ||
                "Unable to delete the item",
              "error"
            );
          });
      },
    });
  };

  const updateEditorContent = (id, html) => {
    setItems((prev) => {
      const updated = prev.map((it) =>
        it.id === id || it._id === id
          ? {
              ...it,
              text: [{ id: it.text?.[0]?.id ?? genId(), content: html }],
              textFormat: "html",
              textRaw: html?.html ?? (typeof html === "string" ? html : " "),
              textPlain: html?.text ?? " ",
            }
          : it
      );
      debouncedSave(updated);
      return updated;
    });
  };

  const updateTableItem = (id, patch) => {
    setItems((prev) =>
      prev.map((it) => (it.id === id || it._id === id ? { ...it, ...patch } : it))
    );
  };

  const onDragEndItems = (result) => {
    if (!result.destination) return;
    const newItems = Array.from(items);
    const [reorderedItem] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, reorderedItem);
    setItems(newItems);
    saveDisclosureData(newItems, true);
  };

  useEffect(() => {
    setOutput(serialize(name, items));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, items]);

  // ── Note composer ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (isComposing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isComposing]);

  const handleAddClick = () => {
    setIsComposing(true);
    setNoteName("");
  };

  const handleCancel = () => {
    setIsComposing(false);
    setNoteName("");
  };

  const handlePost = () => {
    const value = String(noteName || "").trim();
    if (!value) return;
    setNote([value, ...note]);
    setNoteName("");
    setIsComposing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (isEditing) {
        if (!editingId) return;
        addNotes();
      } else {
        addNotes();
      }
    }
    if (e.key === "Escape") handleCancel();
  };

  // ── Sub-note composer ──────────────────────────────────────────────────────
  const [subComposeFor, setSubComposeFor] = useState(null);
  const [subNoteName, setSubNoteName] = useState("");
  const subInputRef = useRef(null);

  useEffect(() => {
    if (subComposeFor !== null && subInputRef.current) {
      subInputRef.current.focus();
    }
  }, [subComposeFor]);

  const openSubComposer = (noteIndex) => {
    setSubComposeFor(noteIndex);
    setSubNoteName("");
  };

  const cancelSubComposer = () => {
    setSubComposeFor(null);
    setSubNoteName("");
  };

  const postSubnote = () => {
    const value = subNoteName.trim();
    if (!value || subComposeFor === null) return;
    setNotes((prev) => {
      const next = [...prev];
      const target = { ...next[subComposeFor] };
      const subNotes = Array.isArray(target.subNotes)
        ? [...target.subNotes]
        : [];
      subNotes.push({ name: value });
      target.subNotes = subNotes;
      next[subComposeFor] = target;
      return next;
    });
    setSubNoteName("");
    setSubComposeFor(null);
  };

  const addSub = (note_id) => {
    const value = subNoteName.trim();
    if (!value) return;

    const payload = { name: subNoteName };

    if (isSubEditing) {
      updateSubDisclosureApiHandler(
        {
          groupingTemplateID: tempId,
          disclosureID: editingParentId,
          subNoteID: editingSubId,
        },
        payload
      ).then((response) => {
        if (response?.data?.success) {
          setSuccessMessage("Updated successfully");
          setShowSuccessPopup(true);
        }
        setIsSubEditing(false);
        setEditingSubId(null);
        setEditingParentId(null);
        setSubNoteName("");
        setSubComposeFor(null);
        getTemplateDetails();
      });
    } else {
      createSubDisclosureApiHandler(
        { groupingTemplateID: tempId, disclosureID: note_id },
        payload
      ).then((response) => {
        if (response?.data?.success) {
          setSuccessMessage("Added successfully");
          setShowSuccessPopup(true);
          setSubNoteName("");
          setSubComposeFor(null);
          const updatedDisclosures =
            response?.data?.updatedGroupingTemplate?.disclosure ||
            response?.data?.groupingTemplate?.disclosure;
          if (updatedDisclosures) {
            setNotes(updatedDisclosures);
          }
          getTemplateDetails(
            response?.data?.updatedGroupingTemplate ||
            response?.data?.groupingTemplate
          );
        }
      });
    }
  };

  const handleSubKeyDown = (e, parentId) => {
    if (e.key === "Enter") {
      if (isSubEditing) {
        addSub(editingParentId);
      } else {
        addSub(parentId);
      }
    }
    if (e.key === "Escape") cancelSubComposer();
  };

  // ── Save handler ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (handleSave !== "disclosures") return;

    if (!selNoteId) {
      console.log("❌ selNoteId missing");
      return;
    }

    setHandleSave(null);
    saveDisclosureData(items, false);
  }, [handleSave, saveDisclosureData, items, setHandleSave, selNoteId]);

  useEffect(() => {
    setNotes(templateData?.disclosure);
  }, [templateData]);

  // Sync cellFormulas from templateData into items
  useEffect(() => {
    if (!templateData?.disclosure || !selNoteId) return;
    const allNotes = templateData.disclosure.flatMap((n) => [
      n,
      ...(n.subNotes || []),
    ]);
    const currentNote = allNotes.find((n) => n._id === selNoteId);
    if (currentNote?.data) {
      setItems((prevItems) =>
        prevItems.map((item) => {
          if (item.type !== "table") return item;
          const matchingBlock = currentNote.data.find(
            (b) =>
              (item._id && b._id === item._id) ||
              b.tableTitle === item.tableTitle
          );
          if (matchingBlock) {
            // Intelligently merge cellFormulas: preserve any local renamed versions
            const incomingFormulas = Array.isArray(matchingBlock.cellFormulas)
              ? matchingBlock.cellFormulas
              : [];
            const mergedFormulas = incomingFormulas.map((f) => {
              const localRenamed = (item.cellFormulas || []).find(
                (ef) =>
                  ef.col === f.col &&
                  (ef.row === f.row || (ef._id && ef._id === f._id))
              );
              return localRenamed ? { ...f, ...localRenamed } : { ...f };
            });

            // Also include any local formulas that haven't synced yet
            (item.cellFormulas || []).forEach((lf) => {
              const alreadyIncluded = mergedFormulas.some(
                (mf) =>
                  String(mf.row).trim().toLowerCase() ===
                    String(lf.row).trim().toLowerCase() &&
                  String(mf.col).trim().toLowerCase() ===
                    String(lf.col).trim().toLowerCase()
              );
              if (!alreadyIncluded) {
                mergedFormulas.push({ ...lf });
              }
            });

            return {
              ...item,
              _id: matchingBlock._id || item._id,
              cellFormulas: mergedFormulas,
            };
          }
          return item;
        })
      );
    }
  }, [templateData, selNoteId]);

  useEffect(() => {
    if (!templateData?.disclosure) return;
    const map = {};
    templateData.disclosure.forEach((note) => {
      const allNotes = [note, ...(note.subNotes || [])];
      allNotes.forEach((n) => {
        (n.data || []).forEach((block) => {
          if (block.dataType !== "table") return;
          const blockId = block._id || block.id;
          (block.cellFormulas || []).forEach((f) => {
            if (!f?.row) return;
            const rowStr = String(f.row).trim();
            const colStr = String(f.col).trim();

            // 1. Primary API key: tableId-rowName-colName
            map[`${blockId}-${rowStr}-${colStr}`] = f;

            // 2. Map row index coordinates if matched in tableRow
            if (Array.isArray(block.tableRow)) {
              const rIdx = block.tableRow.findIndex(
                (r) =>
                  String(r?.name || "").trim().toLowerCase() ===
                  rowStr.toLowerCase()
              );
              if (rIdx >= 0) {
                map[`${blockId}-${rIdx + 1}-${colStr}`] = f;
                const colNum =
                  colStr === "currentYear"
                    ? 1
                    : colStr === "previousYear"
                    ? 2
                    : null;
                if (colNum) {
                  map[`${blockId}-${rIdx + 1}-${colNum}`] = f;
                }
              }
            }
          });
        });
      });
    });
    setLinkedCells((prev) => ({
      ...prev,
      ...map,
    }));
  }, [templateData]);

  // ── Add / update note ──────────────────────────────────────────────────────
  const addNotes = () => {
    const value = String(noteName || "").trim();
    if (!value) return;

    const payload = { name: noteName };

    if (isEditing) {
      updateDisclosureApiHandler(
        { groupingTemplateID: tempId, disclosureID: editingId },
        payload
      ).then((response) => {
        if (response?.data?.success) {
          setSuccessMessage("Updated successfully");
          setShowSuccessPopup(true);
        }
        setIsEditing(false);
        setEditingId(null);
        setNoteName("");
        setIsComposing(false);
        getTemplateDetails();
      });
    } else {
      createDisclosureApiHandler({ groupingTemplateID: tempId }, payload).then(
        (response) => {
          if (response?.data?.success) {
            setSuccessMessage("Added successfully");
            setShowSuccessPopup(true);
            const updatedDisclosures =
              response?.data?.updatedGroupingTemplate?.disclosure ||
              response?.data?.groupingTemplate?.disclosure;
            if (updatedDisclosures && updatedDisclosures.length > 0) {
              setNotes(updatedDisclosures);
              const newNote =
                updatedDisclosures[updatedDisclosures.length - 1];
              setItems(normalizeFromInitial(newNote));
              setActiveItem(`gs_${updatedDisclosures.length - 1}`);
              setSelNote("general");
              setSelNoteId(newNote._id);
              setSelParentId(null);
              setOutput(newNote);
              setName(newNote.name);
            }
            getTemplateDetails(
              response?.data?.updatedGroupingTemplate ||
                response?.data?.groupingTemplate
            );
          }
          setNoteName("");
          setIsComposing(false);
        }
      );
    }
  };

  const resetItems = () => {
    setItems(normalizeFromInitial(null));
    setActiveItem(null);
    setSelNote(null);
    setSelNoteId(null);
    setSelParentId(null);
  };

  // ── Delete handlers ────────────────────────────────────────────────────────
  const deleteNoteHandler = (id) => {
    setDeleteConfirm({
      title: "Confirm deletion of disclosure data?",
      message: "Once deleted, you will not be able to recover this!",
      onConfirm: () => {
        setDeleteConfirm(null);
        deleteDisclosureApiHandler({
          groupingTemplateID: tempId,
          disclosureID: id,
        })
          .then((response) => {
            if (response?.data?.success === true) {
              setSuccessMessage("Deleted successfully");
              setShowSuccessPopup(true);
              resetItems();
              getTemplateDetails();
            }
          })
          .catch((error) => {
            swal(
              "Error",
              error?.response?.data?.error || "Unable to delete the data",
              "error"
            );
          });
      }
    });
  };

  const deleteSubNoteHandler = (id, subid) => {
    setDeleteConfirm({
      title: "Confirm deletion of disclosure data?",
      message: "Once deleted, you will not be able to recover this!",
      onConfirm: () => {
        setDeleteConfirm(null);
        deleteSubDisclosureApiHandler({
          groupingTemplateID: tempId,
          disclosureID: id,
          subNoteID: subid,
        })
          .then((response) => {
            if (response?.data?.success === true) {
              setSuccessMessage("Deleted successfully");
              setShowSuccessPopup(true);
              resetItems();
              getTemplateDetails();
            }
          })
          .catch((error) => {
            swal(
              "Error",
              error?.response?.data?.error || "Unable to delete the data",
              "error"
            );
          });
      }
    });
  };

  // ── Inline update handlers ─────────────────────────────────────────────────
  const handleUpdateNote = (id, value) => {
    if (!String(value || "").trim()) return;

    updateDisclosureApiHandler(
      { groupingTemplateID: tempId, disclosureID: id },
      { name: value }
    )
      .then((res) => {
        if (res?.data?.success) {
          setSuccessMessage("Updated successfully");
          setShowSuccessPopup(true);
          getTemplateDetails();
          setEditingNoteId(null);
        }
      })
      .catch(() => {});
  };

  const handleUpdateSubNote = (parentId, subId, value) => {
    if (!String(value || "").trim()) return;

    updateSubDisclosureApiHandler(
      {
        groupingTemplateID: tempId,
        disclosureID: parentId,
        subNoteID: subId,
      },
      { name: value }
    )
      .then((res) => {
        if (res?.data?.success) {
          setSuccessMessage("Updated successfully");
          setShowSuccessPopup(true);
          getTemplateDetails();
          setEditingSubId(null);
        }
      })
      .catch(() => {});
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
        <motion.div
          initial={{ x: 500, y: 500, opacity: 0 }}
          animate={{ x: 0, y: 0, opacity: 1 }}
          exit={{ x: 0, y: 0, opacity: 0 }}
          transition={{ duration: 0.7, ease: "easeInOut" }}
          className={libcss.hierarchyLayout}
          style={{ border: "0px", marginTop: "0px", gap: "10px", paddingLeft: "10px", paddingRight: "20px", paddingTop: "10px", width: "100%", maxWidth: "100%", height: "calc(100% - 40px)", boxSizing: "border-box" }}
        >
          {/* ── Left panel: notes list ──────────────────────────────────────── */}
          <div
            className={`${libcss.notesSec} ${commoncss.hideScroll}`}
            style={{ width: "40%", height: "100%" }}
          >
            <div className={libcss.levelCatDiv} style={{ marginTop: "0px", gap: "10px", height: "100%", boxSizing: "border-box"}}>
              <div className={libcss.levelCat} style={{ height: "100%", display: "flex", flexDirection: "column", boxSizing: "border-box", border: "1px solid #ccd5d3" }}>
                <span className={libcss.notesLabel} style={{ paddingLeft: "15px" }}>
                  <span>Disclosures</span>
                  {!isReadOnly && (
                    <span
                      className={libcss.addNoteLabel}
                      onClick={handleAddClick}
                      style={{ cursor: "pointer" }}
                    >
                      <PlusIcon /> Add
                    </span>
                  )}
                </span>

                <div className={`${libcss.levelCatDiv} ${libcss.notesDiv2}`} style={{ flex: 1, height: "auto", overflowY: "auto" }}>
                  {isComposing && !isReadOnly && (
                    <div className={libcss.notesItem}>
                      <input
                        ref={inputRef}
                        value={noteName}
                        onChange={(e) => setNoteName(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Type your note..."
                        className={libcss.noteInput}
                      />
                      <div className={libcss.lineActions1}>
                        <span
                          className={`${commoncss.clickable}`}
                          onClick={addNotes}
                          disabled={!noteName.trim()}
                          style={{ color: "#205a46", fontWeight: "600", marginTop: "2px", letterSpacing: "0.8px" }}
                        >
                          ADD
                        </span>
                        &ensp;
                        <CloseIcon
                          onClick={handleCancel}
                          width={14}
                          height={14}
                        />
                      </div>
                    </div>
                  )}

                  {notes &&
                    notes.map((noteItem, i) => (
                      <div key={`note-${noteItem._id || i}`} className={(noteItem.subNotes && noteItem.subNotes.length > 0) || subComposeFor === i ? libcss.notesGroupContainer : ""}>
                        <div
                          className={
                            hoveredIndex === i || activeItem === `gs_${i}`
                              ? libcss.notesItem
                              : libcss.notesItemView
                          }
                          onMouseEnter={() => setHoveredIndex(i)}
                          onMouseLeave={() => setHoveredIndex(null)}
                          onClick={() => {
                            setItems(normalizeFromInitial(noteItem));
                            setActiveItem(`gs_${i}`);
                            setSelNote("general");
                            setSelNoteId(noteItem._id);
                            setSelParentId(null);
                            setOutput(noteItem);
                            setName(noteItem.name);
                          }}
                        >
                          {editingNoteId === noteItem._id ? (
                            <>
                              <input
                                value={editingValue}
                                autoFocus
                                onChange={(e) => setEditingValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter")
                                    handleUpdateNote(noteItem._id, editingValue);
                                  if (e.key === "Escape")
                                    setEditingNoteId(null);
                                }}
                                className={libcss.noteInput}
                              />
                              <div className={libcss.lineActions1}>
                                <span
                                  className={`${commoncss.clickable}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleUpdateNote(noteItem._id, editingValue);
                                  }}
                                  disabled={!editingValue.trim()}
                                  style={{ color: "#205a46", fontWeight: "600", marginTop: "2px", letterSpacing: "0.8px" }}
                                >
                                  ADD
                                </span>
                                &ensp;
                                <CloseIcon
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingNoteId(null);
                                  }}
                                  width={14}
                                  height={14}
                                  style={{ cursor: "pointer", marginTop: "2px" }}
                                />
                              </div>
                            </>
                          ) : (
                            <>
                              {noteItem.name}
                              {(hoveredIndex === i || activeItem === `gs_${i}`) &&
                                !isReadOnly && (
                                  <div className={libcss.lineActions1}>
                                    <PlusIcon
                                      role="button"
                                      tabIndex={0}
                                      style={{ cursor: "pointer" }}
                                      onClick={() => openSubComposer(i)}
                                      onKeyDown={(e) =>
                                        (e.key === "Enter" || e.key === " ") &&
                                        openSubComposer(i)
                                      }
                                    />
                                    &ensp;
                                    <EditIcon
                                      width={15}
                                      height={15}
                                      style={{ cursor: "pointer" }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingNoteId(noteItem._id);
                                        setEditingValue(noteItem.name);
                                      }}
                                    />
                                    &ensp;
                                    <CloseIcon
                                      className={commoncss.clickable}
                                      style={{ cursor: "pointer" }}
                                      width={16}
                                      height={16}
                                      onClick={() =>
                                        deleteNoteHandler(noteItem._id)
                                      }
                                    />
                                  </div>
                                )}
                            </>
                          )}
                        </div>

                        {subComposeFor === i && !isReadOnly && (
                          <div className={libcss.subNotesItemView}>
                            <input
                              ref={subInputRef}
                              value={subNoteName}
                              onChange={(e) => setSubNoteName(e.target.value)}
                              onKeyDown={(e) => handleSubKeyDown(e, noteItem._id)}
                              placeholder="Type subnote…"
                              className={libcss.noteInput}
                            />
                            <div className={libcss.lineActions1}>
                              <span
                                className={`${commoncss.clickable}`}
                                onClick={() => addSub(noteItem._id)}
                                disabled={!subNoteName.trim()}
                                style={{ color: "#205a46", fontWeight: "600", marginTop: "2px", letterSpacing: "0.8px" }}
                              >
                                ADD
                              </span>
                              &ensp;
                              <CloseIcon
                                onClick={cancelSubComposer}
                                width={14}
                                height={14}
                              />
                            </div>
                          </div>
                        )}

                        {noteItem?.subNotes?.length > 0 &&
                          noteItem.subNotes.map((subnote, j) => {
                            const subActiveKey = `gs_sn_${i}-${j}`;
                            const subHoverKey = `s-${i}-${j}`;
                            const isActiveOrHover =
                              hoveredIndex === subHoverKey ||
                              activeItem === subActiveKey;

                            return (
                              <div
                                key={`sub-${subnote._id || `${i}-${j}`}`}
                                className={
                                  isActiveOrHover
                                    ? libcss.notesItem
                                    : libcss.subNotesItemView
                                }
                                onMouseEnter={() =>
                                  setHoveredIndex(subHoverKey)
                                }
                                onMouseLeave={() => setHoveredIndex(null)}
                                onClick={() => {
                                  setItems(normalizeFromInitial(subnote));
                                  setActiveItem(subActiveKey);
                                  setSelNote("general_sn");
                                  setSelNoteId(subnote._id);
                                  setSelParentId(noteItem._id);
                                  setOutput(subnote);
                                  setName(subnote.name);
                                }}
                              >
                                {editingSubId === subnote._id ? (
                                  <>
                                    <input
                                      value={editingSubValue}
                                      autoFocus
                                      onChange={(e) => setEditingSubValue(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter")
                                          handleUpdateSubNote(
                                            noteItem._id,
                                            subnote._id,
                                            editingSubValue
                                          );
                                        if (e.key === "Escape")
                                          setEditingSubId(null);
                                      }}
                                      className={libcss.noteInput}
                                    />
                                    <div className={libcss.lineActions1}>
                                      <span
                                        className={`${commoncss.clickable}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleUpdateSubNote(
                                            noteItem._id,
                                            subnote._id,
                                            editingSubValue
                                          );
                                        }}
                                        disabled={!editingSubValue.trim()}
                                        style={{ color: "#205a46", fontWeight: "600", marginTop: "2px", letterSpacing: "0.8px" }}
                                      >
                                        ADD
                                      </span>
                                      &ensp;
                                      <CloseIcon
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingSubId(null);
                                        }}
                                        width={14}
                                        height={14}
                                        style={{ cursor: "pointer", marginTop: "2px" }}
                                      />
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    {subnote.name}
                                    {isActiveOrHover && !isReadOnly && (
                                      <div className={libcss.lineActions1}>
                                        <EditIcon
                                          width={15}
                                          height={15}
                                          style={{ cursor: "pointer" }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setEditingSubId(subnote._id);
                                            setEditingSubValue(subnote.name);
                                          }}
                                        />
                                        &ensp;
                                        <CloseIcon
                                          className={commoncss.clickable}
                                          style={{ cursor: "pointer" }}
                                          width={16}
                                          height={16}
                                          onClick={() =>
                                            deleteSubNoteHandler(
                                              noteItem._id,
                                              subnote._id
                                            )
                                          }
                                        />
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>

          {/* ── Right panel: editor ─────────────────────────────────────────── */}
         <div
          className={libcss.dataDivs}
          style={{
            border: "none",
            padding: "0",
            marginTop: "0px",
            height: "97.5%",
            overflow: "hidden",
          }}
        >
          <div
              className={libcss.levelCatDiv}
              style={{
                paddingLeft: 0,
                paddingRight: 0,
                background: "#ffffff",
                border: "1px solid #ccd5d3",
                borderRadius: "14px",
                padding: "16px",

                height: "100%",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              <DragDropContext onDragEnd={onDragEndItems}>
                <Droppable droppableId="disclosure-items">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      style={{ display: "grid", gap: 20 }}
                    >
                      {items.map((it, idx) => (
                        <Draggable key={it.id} draggableId={it.id} index={idx} isDragDisabled={isReadOnly}>
                          {(dragProvided) => (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              style={{
                                ...dragProvided.draggableProps.style,
                                width: "100%",
                                minWidth: "100%",
                              }}
                            >
                            {/* Content Area */}
                            {it.type === "text" ? (
                              <QuillReactEditor
                                value={it.text?.[0]?.content ?? " "}
                                onChange={(content) => {
                                  updateEditorContent(it.id, content);
                                }}
                                disabled={isReadOnly}
                                dragHandleProps={dragProvided.dragHandleProps}
                                rightControl={
                                  !isReadOnly && (
                                    <DelIcon
                                      style={{ cursor: "pointer", width: 16, height: 16, marginRight: 8 }}
                                      onClick={() => removeItem(it.id)}
                                    />
                                  )
                                }
                              />
                            ) : (
                              <div
                                style={{
                                  border: "1px solid #ccd5d3",
                                  borderRadius: "10px",
                                  overflow: "hidden",
                                  background: "#fff",
                                  width: "100%",
                                }}
                              >
                                {/* Unified Header - Only for tables */}
                                <div
                                  style={{
                                    background: "#f8f9f9",
                                    display: "flex",
                                    alignItems: "center",
                                    padding: "10px 12px",
                                    borderBottom: "1px solid #ccd5d3",
                                    gap: "12px",
                                  }}
                                >
                                  <div {...dragProvided.dragHandleProps} style={{ display: "flex", alignItems: "center" }}>
                                    <img src={MenuIcon} alt="drag" style={{ cursor: "grab", width: 9 }} />
                                  </div>
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      fontSize: "13px",
                                      color: "#205a46",
                                      textTransform: "uppercase",
                                      flex: 1,
                                    }}
                                  >
                                    {it.tableTitle || "Table"}
                                  </span>
                                  {!isReadOnly && (
                                    <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                      <PlusIcon
                                        style={{ cursor: "pointer", width: 18, height: 18 }}
                                        onClick={() => handleAddSubRow(it.id)}
                                        title="Add Row"
                                      />
                                      <EditIcon
                                        style={{ cursor: "pointer", width: 16, height: 16 }}
                                        onClick={() => setForceOpenTableId(it.id)}
                                      />
                                      <DelIcon
                                        style={{ cursor: "pointer", width: 16, height: 16 }}
                                        onClick={() => removeItem(it.id)}
                                      />
                                    </div>
                                  )}
                                </div>

                                <div
                                  style={{
                                    background: "#fff",
                                    padding: 0,
                                  }}
                                >
                                  <TemplateTableGenerator
                                    isDisclosureTable={true}
                                    linkedCells={linkedCells}
                                    tableId={it._id || it.id}
                                    onCellLinkClick={(
                                      row,
                                      col,
                                      tableRows,
                                      currentRowLabel
                                    ) =>
                                      handleCellLinkClick(
                                        row,
                                        col,
                                        it._id || it.id,
                                        it,
                                        currentRowLabel,
                                        tableRows
                                      )
                                    }
                                    forceOpen={it.id === forceOpenTableId}
                                    allowAddRow={!isReadOnly}
                                    onAddRow={() => handleAddSubRow(it.id)}
                                    onDeleteRow={(rowIndex, rowData) =>
                                      handleDeleteTableRow(it.id, rowIndex, rowData)
                                    }
                                    getRowPermissions={getRowPermissions}
                                    onCellBlur={() => {
                                      handleCellBlurAutoSave();
                                    }}
                                    value={{
                                      tableTitle: it.tableTitle,
                                      tableRow: it.tableRow,
                                      tableColumn: it.tableColumn,
                                      tableType: it.tableType,
                                      table: it.tableData || it.table,
                                      hasCellData: it.hasCellData,
                                    }}
                                    onChange={(next, lastChangedRow) => {
                                      handleTableChange(it.id, next, lastChangedRow);
                                    }}
                                    onAfterOpen={() => {
                                      if (forceOpenTableId === it.id)
                                        setForceOpenTableId(null);
                                    }}
                                    disabled={isReadOnly}
                                    hideHeader={true}
                                    allowReorder={true}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>

              {selNote && !isReadOnly && (
                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    alignItems: "center",
                    padding: 12,
                    borderRadius: 12,
                    marginBottom: 12,
                  }}
                >
                  <CirclePlus width={37} />
                  <AddEditor width={140} onClick={handleAddParagraph} />
                  <AddTable width={100} onClick={handleAddTable} />
                </div>
              )}

              {isAddingTable && (
                <TemplateTableGenerator
                  isDisclosureTable={true}
                  forceOpen={true}
                  value={{}}
                  onChange={(next) => {
                    const id = genId();
                    const newTableItem = {
                      id,
                      type: "table",
                      dataType: "table",
                      tableTitle: next.tableTitle,
                      tableRow: next.tableRow,
                      tableColumn: next.tableColumn,
                      hasCellData: next.hasCellData,
                      tableData: next.tableData || next.table || [],
                      tableType: next.hasCellData ? "content" : "table",
                      table: next.tableData || next.table || [],
                      cellFormulas: [],
                    };
                    const nextItems = [...items, newTableItem];
                    setItems(nextItems);
                    saveDisclosureData(nextItems, true);
                    setIsAddingTable(false);
                  }}
                  onCancel={() => setIsAddingTable(false)}
                />
              )}
            </div>
          </div>
        </motion.div>
      <SidePanel
        type="disclosure"
        tempId={tempId}
        isTemplate={true}
        groupingTemplateID={tempId}
        activeCellKey={activeCellKey}
        showLinkPanel={isSidePanelOpen}
        setShowLinkPanel={(val) => {
          setIsSidePanelOpen(val);
          if (!val) setSelectedCell(null);
        }}
        selectedCell={selectedCell}
        disclosureID={selNoteId}
        dataID={selectedDataID}
        table={(() => {
          if (!templateData?.disclosure || !selectedDataID) return null;
          const allNotes = templateData.disclosure.flatMap((n) => [
            n,
            ...(n.subNotes || []),
          ]);
          const matchedNote = allNotes.find((n) => n._id === selNoteId);
          return (matchedNote?.data || []).find((b) => b._id === selectedDataID) || null;
        })()}
        onApply={(data, updatedTemplate) => {
          const finalRow = data?.row || selectedCell?.row;
          const finalCol = data?.col || selectedCell?.col;
          const apiKey =
            finalRow && finalCol && selectedDataID
              ? `${selectedDataID}-${finalRow}-${finalCol}`
              : null;

          setLinkedCells((prev) => {
            const updated = { ...prev };
            if (!data) {
              delete updated[activeCellKey];
              if (apiKey) delete updated[apiKey];
            } else {
              updated[activeCellKey] = data;
              if (apiKey) updated[apiKey] = data;
            }
            return updated;
          });

          if (selectedDataID) {
            setItems((prevItems) => {
              const updatedItems = prevItems.map((item) => {
                if ((item._id || item.id) === selectedDataID) {
                  const existingFormulas = Array.isArray(item.cellFormulas)
                    ? [...item.cellFormulas]
                    : [];
                  const formulaIdx = existingFormulas.findIndex(
                    (f) =>
                      String(f?.row || "").trim().toLowerCase() ===
                        String(finalRow || "").trim().toLowerCase() &&
                      String(f?.col || "").trim().toLowerCase() ===
                        String(finalCol || "").trim().toLowerCase()
                  );
                  if (formulaIdx >= 0) {
                    if (!data) {
                      existingFormulas.splice(formulaIdx, 1);
                    } else {
                      existingFormulas[formulaIdx] = {
                        ...existingFormulas[formulaIdx],
                        ...data,
                        row: finalRow,
                        col: finalCol,
                      };
                    }
                  } else if (data) {
                    existingFormulas.push({
                      ...data,
                      row: finalRow,
                      col: finalCol,
                    });
                  }
                  return { ...item, cellFormulas: existingFormulas };
                }
                return item;
              });
              return updatedItems;
            });
          }

          if (updatedTemplate) {
            setTemplateData(updatedTemplate);
          } else {
            getTemplateDetails();
          }
        }}
        onSaveSuccess={getTemplateDetails}
      />
      <DeletePopup
        isOpen={deleteConfirm !== null}
        title={deleteConfirm?.title}
        message={deleteConfirm?.message}
        onConfirm={deleteConfirm?.onConfirm}
        onCancel={() => setDeleteConfirm(null)}
      />
      <SuccessPopup
        isOpen={showSuccessPopup}
        message={successMessage}
        onClose={() => setShowSuccessPopup(false)}
      />
    </>
  );
}